import { printHtml } from "@/components/inventory/shelfPrint";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmtN = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : d.toLocaleDateString();
};

export const printSalesOrder = (so, company = {}, customer = null) => {
  const rows = (so.lines || []).map((l) => {
    const pallets = l.cases_per_pallet ? l.cases / l.cases_per_pallet : null;
    return `
    <tr>
      <td><b>${esc(l.product_name)}</b>${l.design_name ? `<div>${esc(l.design_name)}</div>` : ""}</td>
      <td>${l.case_pack ? `${Math.round(l.case_pack)}-Pack` : "—"}</td>
      <td class="r">${fmtN(l.cases, 2)}</td>
      <td class="r">${pallets != null ? fmtN(pallets, 2) : "—"}</td>
      <td class="r">${fmtN(l.bottles)}</td>
    </tr>`;
  }).join("");
  const totCases = (so.lines || []).reduce((a, l) => a + (Number(l.cases) || 0), 0);
  const totPallets = (so.lines || []).reduce((a, l) => a + (l.cases_per_pallet ? l.cases / l.cases_per_pallet : 0), 0);
  const totBottles = (so.lines || []).reduce((a, l) => a + (Number(l.bottles) || 0), 0);
  const custLines = [customer?.company_name || so.customer_name, customer?.address, customer?.website].filter(Boolean);
  const fromLines = [company.company_name, company.address_line1, company.address_line2].filter(Boolean);

  const html = `<html><head><title>Order Confirmation ${esc(so.so_number)}</title><style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #0f172a; margin: 32px; font-size: 12px; }
    .top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
    .co { font-size: 12px; line-height: 1.5; color: #334155; }
    .co .name { font-size: 17px; font-weight: 700; color: #0f172a; letter-spacing: -0.02em; }
    .title-block { text-align: right; }
    .title { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 10px; }
    .meta { border-collapse: collapse; margin-left: auto; }
    .meta th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; border: 1px solid #cbd5e1; padding: 3px 10px; background: #f8fafc; }
    .meta td { border: 1px solid #cbd5e1; padding: 4px 10px; text-align: center; font-weight: 600; }
    .parties { display: flex; gap: 24px; margin-bottom: 20px; }
    .party { flex: 1; border: 1px solid #cbd5e1; }
    .party .hdr { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; background: #f1f5f9; padding: 4px 8px; border-bottom: 1px solid #cbd5e1; font-weight: 700; }
    .party .val { padding: 8px; min-height: 44px; line-height: 1.5; }
    .party .val .first { font-weight: 600; }
    table.items { width: 100%; border-collapse: collapse; }
    table.items th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; background: #f1f5f9; border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; }
    table.items td { border: 1px solid #cbd5e1; padding: 8px; vertical-align: top; }
    .r { text-align: right !important; font-variant-numeric: tabular-nums; }
    .totalrow { display: flex; justify-content: flex-end; margin-top: 14px; }
    .total { display: flex; gap: 24px; border-top: 2px solid #0f172a; padding-top: 8px; font-size: 13px; font-weight: 700; }
    .notes { margin-top: 18px; font-size: 11px; color: #475569; }
    .notes .hdr { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; font-weight: 700; margin-bottom: 3px; }
    .foot { margin-top: 36px; font-size: 10px; color: #94a3b8; }
    @media print { body { margin: 12mm; } }
  </style></head><body>
    <div class="top">
      <div class="co">
        <div class="name">${esc(company.company_name || "")}</div>
        <div>${esc(company.address_line1 || "")}</div>
        <div>${esc(company.address_line2 || "")}</div>
      </div>
      <div class="title-block">
        <div class="title">Order Confirmation</div>
        <table class="meta">
          <tr><th>SO Number</th><th>Customer PO</th><th>Order Date</th><th>Requested Ship</th></tr>
          <tr><td>${esc(so.so_number)}</td><td>${esc(so.customer_po || "—")}</td><td>${esc(fmtDate(so.order_date))}</td><td>${esc(fmtDate(so.ship_date))}</td></tr>
        </table>
      </div>
    </div>
    <div class="parties">
      <div class="party"><div class="hdr">Customer</div><div class="val">${custLines.map((l, i) => `<div class="${i === 0 ? "first" : ""}">${esc(l)}</div>`).join("")}</div></div>
      <div class="party"><div class="hdr">From</div><div class="val">${fromLines.map((l, i) => `<div class="${i === 0 ? "first" : ""}">${esc(l)}</div>`).join("")}</div></div>
    </div>
    <table class="items">
      <thead><tr>
        <th style="width:38%">Item</th><th style="width:14%">Pack</th>
        <th class="r" style="width:15%">Cases</th><th class="r" style="width:15%">Pallets</th><th class="r" style="width:18%">Bottles</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totalrow"><div class="total">
      <span>Total</span><span>${fmtN(totCases, 2)} Cases</span>${totPallets ? `<span>${fmtN(totPallets, 2)} Pallets</span>` : ""}<span>${fmtN(totBottles)} Bottles</span>
    </div></div>
    ${so.notes ? `<div class="notes"><div class="hdr">Notes</div>${esc(so.notes)}</div>` : ""}
    <div class="foot">Generated ${new Date().toLocaleString()}</div>
  </body></html>`;
  printHtml(html);
};
